/**
 * Most barcode scanners act as a keyboard: they type the code far faster than a person can, then
 * usually press Enter or Tab. Some send nothing after the code, so a short pause also ends a scan.
 */

/** Scanners average a few milliseconds per key; people rarely type faster than 80ms per key. */
export const SCAN_AVERAGE_KEY_GAP_MS = 50
/**
 * Quiet this long ends a scan from a scanner that sends nothing after the code; Enter or Tab end one
 * at once. It's generous because a busy page can hand over a scan's keys with gaps.
 */
export const SCAN_END_PAUSE_MS = 300
/** Short enough for three-character SKUs such as F01. */
export const SCAN_MIN_LENGTH = 3

/** The parts of a keydown event the detector reads. */
export interface ScanKeyEvent {
  key: string
  altKey: boolean
  ctrlKey: boolean
  metaKey: boolean
  repeat: boolean
  isComposing?: boolean
  /** When the key was pressed. A busy page can handle keys late; this keeps their real spacing. */
  timeStamp?: number
}

export interface BarcodeScanDetector {
  /**
   * Feeds one keydown. Returns true when the key is the Enter or Tab that ends a scan, so the caller
   * can stop it from pressing a focused button or moving focus.
   */
  handleKey: (event: ScanKeyEvent, onScan: (code: string) => void) => boolean
  /** True when the key arrives inside a scan in progress. Call it before `handleKey`. */
  continuesScan: (event: ScanKeyEvent) => boolean
  /** Drops a scan in progress and its pending pause timer. */
  reset: () => void
}

const SCAN_TERMINATOR_KEYS = new Set(['Enter', 'Tab'])
/** Scanners press Shift for capital letters; it doesn't interrupt a scan. */
const IGNORED_KEYS = new Set(['Shift', 'CapsLock'])

export function createBarcodeScanDetector(): BarcodeScanDetector {
  let code = ''
  let firstKeyAt = 0
  let lastKeyAt = 0
  let pauseTimer: ReturnType<typeof setTimeout> | null = null

  const reset = () => {
    if (pauseTimer) {
      clearTimeout(pauseTimer)
      pauseTimer = null
    }

    code = ''
  }

  // Keys typed close together count as a scan when they average scanner speed.
  const takeScannedCode = () => {
    const averageKeyGap = code.length > 1 ? (lastKeyAt - firstKeyAt) / (code.length - 1) : Infinity
    const scannedCode =
      code.length >= SCAN_MIN_LENGTH && averageKeyGap <= SCAN_AVERAGE_KEY_GAP_MS ? code : null
    reset()

    return scannedCode
  }

  const handleKey: BarcodeScanDetector['handleKey'] = (event, onScan) => {
    if (event.altKey || event.ctrlKey || event.metaKey || event.repeat || event.isComposing) {
      reset()
      return false
    }

    if (IGNORED_KEYS.has(event.key)) {
      return false
    }

    if (SCAN_TERMINATOR_KEYS.has(event.key)) {
      const scannedCode = takeScannedCode()

      if (scannedCode === null) {
        return false
      }

      onScan(scannedCode)
      return true
    }

    if (event.key.length !== 1) {
      reset()
      return false
    }

    const now = event.timeStamp ?? Date.now()

    // A page too busy to run the pause timer can hand over the next key first.
    if (code !== '' && now - lastKeyAt > SCAN_END_PAUSE_MS) {
      const scannedCode = takeScannedCode()

      if (scannedCode !== null) {
        onScan(scannedCode)
      }
    }

    if (pauseTimer) {
      clearTimeout(pauseTimer)
    }

    if (code === '') {
      firstKeyAt = now
    }

    code += event.key
    lastKeyAt = now
    pauseTimer = setTimeout(() => {
      const scannedCode = takeScannedCode()

      if (scannedCode !== null) {
        onScan(scannedCode)
      }
    }, SCAN_END_PAUSE_MS)

    return false
  }

  const continuesScan: BarcodeScanDetector['continuesScan'] = (event) =>
    code !== '' && (event.timeStamp ?? Date.now()) - lastKeyAt <= SCAN_END_PAUSE_MS

  return { handleKey, continuesScan, reset }
}
